import { Box, BoxProps, Divider, Stack, Typography, TypographyProps, useTheme } from "@carrotfertility/carotene-core"
import { useFlags } from "launchdarkly-react-client-sdk"
import React from "react"

interface PageHeaderProps extends Omit<TypographyProps, "children"> {
  pageTitle: TypographyProps["children"]
  startGlyph?: React.ReactNode
  endSlot?: React.ReactNode
  /**
   * Drop the rule beneath the title, for a page that already draws one there itself.
   *
   * Added for Messages, whose tab bar sits directly under the title and whose Carotene theme styling
   * supplies the design's bottom rule. Keeping both draws two rules a few pixels apart.
   *
   * Defaults to `false`: every other page in the app wants the rule, and none of them pass this.
   */
  hideDivider?: boolean
}

export function PageHeaderGlyph(props: BoxProps<"img">) {
  return <Box alt="" component="img" height="2.25rem" width="2.25rem" {...props} />
}

export function PageBreadcrumbHeader({
  breadcrumbs,
  search = null
}: {
  breadcrumbs: React.ReactNode
  search?: React.ReactNode
}) {
  const { showCarrotAcademySearch } = useFlags()
  return (
    <div>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}
        padding={(theme) => theme.spacing(theme.tokens.spacing.md, 0, theme.tokens.spacing.xs)}
      >
        {breadcrumbs}
        {search && showCarrotAcademySearch ? search : null}
      </Stack>
      <Divider />
    </div>
  )
}

export function PageHeader({ pageTitle, endSlot, hideDivider = false, ...otherProps }: PageHeaderProps) {
  const theme = useTheme()
  return (
    <div>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={theme.spacing(theme.tokens.spacing.md)}
        padding={(theme) => theme.spacing(theme.tokens.spacing.md, 0, theme.tokens.spacing.xs)}
      >
        <Box
          flexWrap="wrap"
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap={theme.spacing(theme.tokens.spacing.md)}
        >
          <Typography variant="h1" {...otherProps}>
            {pageTitle}
          </Typography>
        </Box>
        {endSlot}
      </Box>
      {!hideDivider && <Divider />}
    </div>
  )
}
